
import Link from '@/components/compat/Link';
import { usePathname } from '@/lib/navigation';
import { useMemo, useState } from 'react';
import { ChevronDown, ExternalLink, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { parseChangelogProductFromPath } from '@/components/painel/changelog/changelog-painel-utils';
import { getChangelogSidebarChildren } from '@/components/painel/changelog/changelog-nav-config';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { getNavGroupsForRole } from '@/lib/painel-nav-rbac';
import { getNavKeysForRole } from '@/lib/painel-rbac';
import type { PainelNavItem } from './painel-nav';

type PainelSidebarProps = {
  mobileOpen: boolean;
  onCloseMobile: () => void;
};

function isNavItemActive(item: Pick<PainelNavItem, 'href' | 'matchExact'>, pathname: string): boolean {
  const { href, matchExact } = item;
  if (href === '/painel') return pathname === '/painel';
  if (matchExact) {
    return pathname === href || pathname === `${href}/`;
  }
  if (href.includes('/painel/changelog/') && (href.endsWith('/overview') || href.endsWith('/versions') || href.endsWith('/categories'))) {
    return pathname === href || pathname === `${href}/`;
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

type SectionKey = 'content' | 'management' | 'analytics' | 'changelog' | 'system';

const ALL_SECTIONS_OPEN: Record<SectionKey, boolean> = {
  content: true,
  management: true,
  analytics: true,
  changelog: true,
  system: true,
};

const navScrollClass = cn(
  'min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-3 pb-3',
  'scroll-smooth',
  '[scrollbar-width:thin]',
  '[scrollbar-color:rgba(56,189,248,0.4)_rgba(255,255,255,0.06)]',
  '[&::-webkit-scrollbar]:w-2',
  '[&::-webkit-scrollbar-track]:my-2 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-white/[0.04]',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-sky-400/35',
  'hover:[&::-webkit-scrollbar-thumb]:bg-sky-300/50',
  '[&::-webkit-scrollbar-thumb]:min-h-[40px]'
);

export function PainelSidebar({ mobileOpen, onCloseMobile }: PainelSidebarProps) {
  const pathname = usePathname();
  const { role } = usePainelSession();
  const [sectionsOpen, setSectionsOpen] = useState<Record<SectionKey, boolean>>(() => ({ ...ALL_SECTIONS_OPEN }));

  const navGroups = useMemo(() => {
    if (!role) return [];
    const allowed = new Set(getNavKeysForRole(role));
    const changelogProduct = parseChangelogProductFromPath(pathname) ?? 'xat';
    return getNavGroupsForRole(role)
      .map((group) => {
        if (group.id !== 'changelog') return group;
        const items: PainelNavItem[] = getChangelogSidebarChildren(changelogProduct)
          .filter((c) => allowed.has(c.key as PainelNavItem['key']))
          .map((c) => ({
            key: c.key as PainelNavItem['key'],
            label: c.label,
            href: c.href,
            icon: c.icon,
          }));
        if (items.length === 0) return null;
        return { ...group, items };
      })
      .filter((g): g is NonNullable<typeof g> => g !== null);
  }, [pathname, role]);

  const toggleSection = (key: SectionKey) => {
    setSectionsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sidebarInner = (
    <>
      <div className="shrink-0 px-4 pb-4 pt-2 lg:block">
        <div className="flex items-center justify-between gap-2 overflow-visible">
          <Link
            href="/painel"
            className="group inline-flex min-w-0 max-w-full flex-col gap-1 pt-0.5"
            onClick={onCloseMobile}
          >
            <span className="relative inline-block w-fit">
              <span className="whitespace-nowrap font-poppins text-3xl font-bold leading-none tracking-tight text-white transition-colors group-hover:text-blue-100 sm:text-4xl">
                xat Ajuda
              </span>
              <span
                className={cn(
                  'absolute right-0 top-0 z-10',
                  'translate-x-[calc(58%+3px)] -translate-y-[calc(42%+4px)]',
                  'inline-flex items-center justify-center rounded-full',
                  'px-2 py-[2px] font-poppins text-[8px] font-bold uppercase leading-none tracking-[0.1em] text-white',
                  'bg-gradient-to-br from-sky-400 to-[#000b6a] shadow-md shadow-sky-950/50 ring-1 ring-white/25',
                  'sm:px-2 sm:py-0.5 sm:text-[9px] sm:tracking-[0.12em]'
                )}
                title="ÁREA 42"
              >
                ÁREA 42
              </span>
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300/80">Painel</span>
          </Link>
          <button
            type="button"
            className="rounded-lg p-2 text-blue-100/80 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
            onClick={onCloseMobile}
            aria-label="Fechar menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <nav aria-label="Navegação principal" className={cn('flex min-h-0 flex-1 flex-col px-0')}>
        <div className={navScrollClass}>
          {navGroups.map((group, groupIndex) => (
            <div key={group.id}>
              <div
                className={cn(
                  groupIndex > 0 && 'mt-4 border-t border-white/10 pt-4',
                  groupIndex === 0 && 'pt-0.5'
                )}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(group.id)}
                  className="mb-2 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-white/[0.05]"
                  aria-expanded={sectionsOpen[group.id]}
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-sky-300/70 transition-transform duration-200',
                      !sectionsOpen[group.id] && '-rotate-90'
                    )}
                  />
                  <span className="font-poppins text-[10px] font-bold uppercase tracking-[0.16em] text-sky-300/55">
                    {group.label}
                  </span>
                </button>

                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-out',
                    sectionsOpen[group.id] ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className={cn(sectionsOpen[group.id] ? 'overflow-visible' : 'overflow-hidden')}>
                    <div className="flex flex-col gap-1">
                      {group.items.map(({ key, label, href, icon: Icon, matchExact }) => {
                        const active = isNavItemActive({ href, matchExact }, pathname);
                        return (
                          <Link
                            key={key}
                            href={href}
                            onClick={onCloseMobile}
                            className={cn(
                              'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 font-poppins text-sm font-medium transition-all duration-300',
                              active
                                ? cn(
                                    'bg-gradient-to-r from-sky-500/12 via-white/[0.08] to-white/[0.04] text-white',
                                    'shadow-[0_0_32px_-8px_rgba(56,189,248,0.35)] ring-1 ring-sky-400/25'
                                  )
                                : 'text-blue-100/85 hover:bg-white/[0.06] hover:text-white'
                            )}
                          >
                            {active ? (
                              <span
                                className="pointer-events-none absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r-full bg-gradient-to-b from-sky-300 to-indigo-500 shadow-[0_0_12px_rgba(56,189,248,0.5)]"
                                aria-hidden
                              />
                            ) : null}
                            <Icon
                              className={cn(
                                'relative z-[1] h-[18px] w-[18px] shrink-0 transition-transform duration-200',
                                active
                                  ? 'text-sky-200 drop-shadow-[0_0_8px_rgba(56,189,248,0.35)]'
                                  : 'text-slate-300 group-hover:scale-105'
                              )}
                            />
                            <span className="relative z-[1]">{label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </nav>

      <div className="shrink-0 border-t border-white/10 px-3 pt-4">
        <Link
          href="/"
          className={cn(
            'flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 font-poppins text-xs font-semibold text-blue-100 transition-all duration-300',
            'hover:border-sky-400/30 hover:bg-white/[0.10] hover:text-white hover:shadow-[0_0_24px_-10px_rgba(56,189,248,0.25)]'
          )}
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" />
          Ver site público
        </Link>
      </div>
    </>
  );

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-[#030023]/70 backdrop-blur-sm transition-opacity duration-200 lg:hidden',
          mobileOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        )}
        aria-hidden={!mobileOpen}
        onClick={onCloseMobile}
      />

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-white/10 bg-gradient-to-b from-[#030023] via-[#030023] to-[#000b6a]/95 py-6 shadow-xl shadow-black/40 transition-transform duration-300 ease-out',
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {sidebarInner}
      </aside>
    </>
  );
}
